import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';

interface WebsiteInfo {
  site: { slug: string; published: boolean; tagline: string | null; phone: string | null; hours: string | null; show_out_of_stock: boolean; ordering_enabled: boolean } | null;
  suggestedSlug: string;
  items: number;
  in_stock: number;
}

export default function Website() {
  const { data, error, reload } = useLoad(() => api<WebsiteInfo>('/api/website'), []);
  const [form, setForm] = useState({ slug: '', tagline: '', phone: '', hours: '', showOutOfStock: false, orderingEnabled: false });
  const [preview, setPreview] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data) return;
    const s = data.site;
    setForm({ slug: s?.slug ?? data.suggestedSlug, tagline: s?.tagline ?? '', phone: s?.phone ?? '', hours: s?.hours ?? '', showOutOfStock: s?.show_out_of_stock ?? false, orderingEnabled: s?.ordering_enabled ?? false });
    if (s) loadPreview();
  }, [data]);

  async function loadPreview() {
    const r = await api<Response>('/api/website/preview', { raw: true });
    if (r.ok) setPreview(await r.text());
  }

  async function save(published: boolean, done: string) {
    setErr(null); setMsg(''); setBusy(true);
    try {
      await api('/api/website', { method: 'PUT', body: { ...form, published } });
      setMsg(done);
      reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  if (error) return <ErrorNote error={error} />;
  if (!data) return <div className="empty">Loading…</div>;
  const site = data.site;
  const url = `${location.origin}/s/${site?.slug ?? form.slug}`;
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });

  return (
    <div className="stack" style={{ maxWidth: 980 }}>
      <div className="page-head"><div className="grow">
        <h1>Create my website</h1>
        <p>Your store gets its own web page that lists what you have on the shelf and your prices. It reads straight from the register, so when you change a price or sell the last bottle, the website changes too.</p>
      </div></div>
      <ErrorNote error={err} />
      {msg && <div className="notice good">{msg}</div>}

      {site && (
        <div className="panel"><div className="panel-body row wrap">
          <div className="grow stack-s">
            <div>{site.published ? <span className="chip good">Live</span> : <span className="chip">Switched off</span>} Your website address</div>
            <a className="num" href={url} target="_blank" rel="noreferrer">{url}</a>
          </div>
          <button onClick={() => { navigator.clipboard?.writeText(url); setMsg('Link copied.'); }}>Copy link</button>
          {site.published
            ? <button disabled={busy} onClick={() => save(false, 'Your website is switched off. Visitors see “not found” until you switch it back on.')}>Switch off</button>
            : <button className="primary" disabled={busy} onClick={() => save(true, 'Your website is live again.')}>Switch on</button>}
        </div></div>
      )}

      <div className="panel"><div className="panel-head"><h2>{site ? 'Website details' : 'Set up your website'}</h2></div><div className="panel-body stack">
        <p className="muted">
          {data.in_stock} of your {data.items} items are in stock and will be shown{form.showOutOfStock ? ', plus the rest marked “Out of stock”' : ''}.
          Your store name and address come from Settings. Visitors must say they are 21 or older before they see the page.
        </p>
        <div className="field-row">
          <label>Web address<input value={form.slug} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} /></label>
          <label>Phone (optional)<input value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="(201) 555-0100" /></label>
        </div>
        <p className="faint">Your page will be at <span className="num">{location.origin}/s/{form.slug || '…'}</span></p>
        <label>Short welcome line (optional)<input value={form.tagline} maxLength={160} onChange={(e) => set({ tagline: e.target.value })} placeholder="Family-owned since 1985. Big craft beer selection." /></label>
        <label>Opening hours (optional)<input value={form.hours} maxLength={160} onChange={(e) => set({ hours: e.target.value })} placeholder="Mon–Sat 9am–10pm, Sun 10am–7pm" /></label>
        <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}>
          <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={form.showOutOfStock} onChange={(e) => set({ showOutOfStock: e.target.checked })} />
          Also list items that are out of stock
        </label>
        <label className="row" style={{ display: 'flex', fontWeight: 500, color: 'var(--ink)' }}>
          <input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={form.orderingEnabled} onChange={(e) => set({ orderingEnabled: e.target.checked })} />
          Let customers order online and pick up in the store
        </label>
        {form.orderingEnabled && (
          <div className="notice warn">
            Customers reserve items on the website. Nothing is charged online: they pay at the register when they pick up, and the cashier scans their ID like any other sale.
            Only hand the order to the person named on it, who must be 21 or older and not intoxicated. New orders show up under <b>Web orders</b>.
          </div>
        )}
        <div>
          {site
            ? <button className="primary" disabled={busy || form.slug.length < 3} onClick={() => save(site.published, 'Saved. Your website is updated.')}>Save changes</button>
            : <button className="primary" disabled={busy || form.slug.length < 3} onClick={() => save(true, 'Your website is live! Share the link with your customers.')}>Create my website</button>}
        </div>
      </div></div>

      {site && (
        <div className="panel"><div className="panel-head"><h2 className="grow">Preview</h2><button className="ghost" onClick={loadPreview}>Refresh</button></div>
          <iframe title="Website preview" sandbox="allow-scripts" srcDoc={preview} style={{ width: '100%', height: 640, border: 0, display: 'block', borderRadius: '0 0 8px 8px' }} />
        </div>
      )}
    </div>
  );
}
